import React from 'react';
import Overlay_popup from '../../general_components/Overlay_popup/Overlay_popup';
import MasterButton from '../../general_components/MasterButton/MasterButton';
import GGApi from '../../../utils/GGApi';
import validations from '../../../utils/validations';
import { logMonthlyInviteEmailSent, logMonthlyInviteCopyLink } from '../../../utils/monthlyGiftAnalytics';

import './Monthly_invite_modal.css';

import email_sent_illustration from '../../../images/illustrations/personalized_gift.png';

const SEND_COOLDOWN_MS = 10000;

/**
 * Monthly Gifts invite modal. Two stages - form and success.
 *
 * Props:
 *   - open            : boolean — render modal when true
 *   - onClose         : () => void — fires when user clicks X, Done, or backdrop
 *   - plan_id         : number — passed to the BE invite endpoint
 *   - child_name      : string — the recipient kid's display name (for copy)
 *   - parent_name     : string — current user's first name (for preview)
 *   - monthly_link    : string — prefilled give-a-gift link used by "Copy Link Instead"
 */

class Monthly_invite_modal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            stage: 'form',
            recipient_name: '',
            recipient_email: '',
            // Send-button lifecycle: `sending` while request in flight,
            // `cooled` for SEND_COOLDOWN_MS after completion (success or fail).
            sending: false,
            cooled: false,
            // Error string shown inline above the SEND INVITE button when present.
            error: '',
            // Snapshot of last successful send so the success stage can display
            // the recipient's name + use the BE-returned link for copy actions.
            success_recipient_name: '',
            success_link: '',
        };
        // Track timeout id so we can clear it on unmount or on modal close.
        this._cooldownTimer = null;

        this.handleClose         = this.handleClose.bind(this);
        this.handleChange        = this.handleChange.bind(this);
        this.handleSend          = this.handleSend.bind(this);
        this.handleCopyFormLink  = this.handleCopyFormLink.bind(this);
        this.handleCopySuccessLink = this.handleCopySuccessLink.bind(this);
    }

    componentWillUnmount() {
        if (this._cooldownTimer) {
            clearTimeout(this._cooldownTimer);
            this._cooldownTimer = null;
        }
    }

    // Reset everything to a fresh form before bubbling close up so reopening
    // doesn't show a stale success screen or partially-filled inputs.
    handleClose() {
        if (this._cooldownTimer) {
            clearTimeout(this._cooldownTimer);
            this._cooldownTimer = null;
        }
        this.setState({
            stage: 'form',
            recipient_name: '',
            recipient_email: '',
            sending: false,
            cooled: false,
            error: '',
            success_recipient_name: '',
            success_link: '',
        });
        if (typeof this.props.onClose === 'function') this.props.onClose();
    }

    handleChange(e) {
        const { name, value } = e.target;
        this.setState({ [name]: value, error: '' });
    }

    handleSend() {
        if (this.state.sending || this.state.cooled) return;
        const recipient_name = String(this.state.recipient_name || '').trim();
        const recipient_email = String(this.state.recipient_email || '').trim();

        // Client-side validation.
        if (!recipient_name) {
            this.setState({ error: "Please enter the recipient's name." });
            return;
        }
        if (!recipient_email || !validations.validateEmail(recipient_email)) {
            this.setState({ error: 'Please enter a valid recipient email address.' });
            return;
        }

        this.setState({ sending: true, error: '' });
        GGApi.send_monthly_gift_invite(this.props.plan_id, recipient_name, recipient_email)
            .then((res) => {
                // Always start the cooldown regardless of success/failure so a
                // server hiccup or validation error can't be hammered.
                this.startCooldown();
                if (res && res.success) {
                    logMonthlyInviteEmailSent({ plan_id: this.props.plan_id });
                    this.setState({
                        sending: false,
                        stage: 'success',
                        success_recipient_name: recipient_name,
                        success_link: (res && res.link) ? res.link : (this.props.monthly_link || ''),
                    });
                } else {
                    this.setState({
                        sending: false,
                        error: (res && res.message) ? res.message : 'Something went wrong. Please try again.',
                    });
                }
            })
            .catch(() => {
                this.startCooldown();
                this.setState({
                    sending: false,
                    error: 'Something went wrong. Please try again.',
                });
            });
    }

    startCooldown() {
        if (this._cooldownTimer) clearTimeout(this._cooldownTimer);
        this.setState({ cooled: true });
        this._cooldownTimer = setTimeout(() => {
            this._cooldownTimer = null;
            this.setState({ cooled: false });
        }, SEND_COOLDOWN_MS);
    }

    handleCopyFormLink() {
        const link = this.props.monthly_link || '';
        if (!link) return;
        logMonthlyInviteCopyLink({ source: 'form stage', plan_id: this.props.plan_id });
        navigator.clipboard.writeText(link)
            .then(() => {
                // After the user confirms the copy alert, close the overlay so they
                // can go paste the link wherever they intended without an extra tap.
                alert('The monthly gift link was copied!');
                this.handleClose();
            })
            .catch(() => alert('There was an issue when attempting to copy the link.'));
    }

    handleCopySuccessLink() {
        const link = this.state.success_link || this.props.monthly_link || '';
        if (!link) return;
        logMonthlyInviteCopyLink({ source: 'success stage', plan_id: this.props.plan_id });
        navigator.clipboard.writeText(link)
            .then(() => alert('The monthly gift link was copied!'))
            .catch(() => alert('There was an issue when attempting to copy the link.'));
    }

    // Send-button label is purely a function of state — no countdown per
    // product decision; just 'SENDING…' while in-flight and 'SENT' during cooldown.
    sendButtonLabel() {
        if (this.state.sending) return 'SENDING…';
        if (this.state.cooled) return 'SENT';
        return 'SEND INVITE';
    }

    renderFormStage() {
        const { child_name, parent_name } = this.props;
        const childDisplay = (child_name || '').trim() || 'them';
        const parentDisplay = (parent_name || '').trim() || 'Someone';
        // Live preview values fall back to mockup-style placeholders when empty
        // so the preview always reads naturally (and matches the Figma mock).
        const previewName = (this.state.recipient_name || '').trim() || 'there';
        return (
            <div className='monthly_invite_modal_body'>
                <p className='monthly_invite_subtitle'>
                    Send a quick email invite with {childDisplay}'s monthly gift link.
                </p>

                {/* Inputs */}
                <div className='monthly_invite_field'>
                    <label className='monthly_invite_field_label' htmlFor='monthly_invite_name'>Send to:</label>
                    <input
                        id='monthly_invite_name'
                        type='text'
                        name='recipient_name'
                        placeholder='Name'
                        maxLength='80'
                        autoComplete='off'
                        value={this.state.recipient_name}
                        onChange={this.handleChange}
                        className='monthly_invite_input'
                    />
                </div>
                <div className='monthly_invite_field'>
                    <label className='monthly_invite_field_label' htmlFor='monthly_invite_email'>Email:</label>
                    <input
                        id='monthly_invite_email'
                        type='email'
                        name='recipient_email'
                        placeholder="Recipient's email"
                        autoComplete='off'
                        value={this.state.recipient_email}
                        onChange={this.handleChange}
                        className='monthly_invite_input'
                    />
                </div>

                {/* Live email preview — mint outer card wrapping a white inner card */}
                <div className='monthly_invite_preview_outer'>
                    <h3 className='monthly_invite_preview_header'>Email Preview</h3>
                    <div className='monthly_invite_preview_inner'>
                        <p className='monthly_invite_preview_line'>Hi {previewName},</p>
                        <p className='monthly_invite_preview_line'>
                            {parentDisplay} invited you to set up a monthly gift for {childDisplay}'s future.
                        </p>
                        <p className='monthly_invite_preview_line'>
                            Monthly gifting is for the closest circle who want to contribute regularly over time.
                        </p>
                        <div className='monthly_invite_preview_cta_wrapper'>
                            <span className='monthly_invite_preview_cta'>START A MONTHLY GIFT</span>
                        </div>
                    </div>
                </div>

                {this.state.error ? (
                    <p className='monthly_invite_error'>{this.state.error}</p>
                ) : null}

                {/* Primary CTA — full-width yellow button + greyed during sending/cooldown */}
                <div className='monthly_invite_send_btn_wrapper'>
                    <MasterButton
                        buttonText={this.sendButtonLabel()}
                        handleButtonClick={this.handleSend}
                        notSticky={true}
                        inactive_button={this.state.sending || this.state.cooled}
                        disabled={this.state.sending || this.state.cooled}
                    />
                </div>

                {/* Secondary text link */}
                <p className='monthly_invite_text_link' onClick={this.handleCopyFormLink}>
                    Copy Link Instead
                </p>
            </div>
        );
    }

    renderSuccessStage() {
        const { child_name } = this.props;
        const childDisplay = (child_name || '').trim() || 'them';
        const recipient = (this.state.success_recipient_name || '').trim() || 'them';
        return (
            <div className='monthly_invite_modal_body monthly_invite_success_body'>
                <img
                    src={email_sent_illustration}
                    alt=''
                    className='monthly_invite_success_illustration'
                />
                <p className='monthly_invite_success_text'>
                    We sent {recipient} an email with {childDisplay}'s monthly gift link.
                </p>

                <div className='monthly_invite_send_btn_wrapper'>
                    <MasterButton
                        buttonText='COPY MONTHLY GIFT LINK'
                        handleButtonClick={this.handleCopySuccessLink}
                        notSticky={true}
                    />
                </div>

                <p className='monthly_invite_text_link' onClick={this.handleClose}>
                    Done
                </p>
            </div>
        );
    }

    render() {
        // Title swaps between stages; X close behavior is identical (handleClose
        // resets state + bubbles up). Overlay_popup handles backdrop + headline row.
        const overlay_title = this.state.stage === 'success' ? 'Email Sent!' : 'Invite with Email';
        return (
            <Overlay_popup
                open={!!this.props.open}
                closer={true}
                handleClose={this.handleClose}
                overlay_title={overlay_title}
                wide={true}
                joker={this.state.stage === 'success' ? this.renderSuccessStage() : this.renderFormStage()}
            />
        );
    }
}

export default Monthly_invite_modal;
